import { useState } from 'react';

const LOGO_SRC =
  (import.meta.env.VITE_LOGO_SRC as string | undefined) ?? '/logo.png';
const BRAND_NAME =
  (import.meta.env.VITE_BRAND_NAME as string | undefined) ??
  'Neuro Agents Agency';

/**
 * Top-centre brand mark above the hero headline.
 *
 * The brand WORD-mark intentionally lives only in the page <title>, hero pill,
 * and footer — the visual mark here speaks for itself. Showing the same text
 * under the logo creates a clunky "stacked-name" effect on the landing.
 *
 * For best visual result, `app/public/logo.png` should be a transparent PNG.
 * Opaque-square logos will glow as a square — re-export with alpha for the
 * mark-following glow effect.
 */
export default function HeroLogo() {
  const [status, setStatus] = useState<'idle' | 'loaded' | 'failed'>('idle');

  if (status === 'failed') return null;

  return (
    <div className="reveal opacity-0 flex flex-col items-center mb-6 sm:mb-8 lg:mb-10">
      <div
        className="group"
        style={{
          // Stronger cyan glow tracing the alpha silhouette — premium feel.
          filter:
            status === 'loaded'
              ? 'drop-shadow(0 14px 44px rgba(0,212,255,0.50)) drop-shadow(0 0 110px rgba(0,212,255,0.22))'
              : 'none',
          transition: 'filter 0.5s cubic-bezier(0.16, 1, 0.3, 1)',
        }}
      >
        <picture>
          <source srcSet="/logo.webp" type="image/webp" />
          <img
            src={LOGO_SRC}
            alt={`${BRAND_NAME} — логотип`}
            width={512}
            height={512}
            fetchPriority="high"
            decoding="async"
            onLoad={() => setStatus('loaded')}
            onError={() => setStatus('failed')}
            className={[
              // Premium scale: phone 176, tablet 240, desktop 320, xl 384 px.
              'h-44 w-44 sm:h-60 sm:w-60 lg:h-80 lg:w-80 xl:h-96 xl:w-96',
              'object-contain will-change-transform',
              'group-hover:scale-[1.04]',
              status === 'loaded' ? 'opacity-100' : 'opacity-0',
            ].join(' ')}
            style={{
              transition:
                'transform 500ms cubic-bezier(0.16,1,0.3,1), opacity 350ms ease',
            }}
          />
        </picture>
      </div>
    </div>
  );
}
